/**
 * Configuring who actually sends the mail.
 *
 * `POST /v1/providers` had existed since Phase 3 with no dashboard caller at
 * all — `grep -rn "v1/providers" web/src` returned nothing. The only provider
 * configuration that existed in practice was the `dev` one `seed.go` writes
 * with raw SQL, so a self-hosted install could not configure sending without
 * minting an API key and reaching for curl.
 *
 * That is the third time this shape has appeared here: Groups had a picker and
 * no way to make one, Teams had five endpoints and no screen. It is also the
 * blind spot that hid the provider-registry bug, where seeding a `dev` config
 * meant local sending took the one path that worked.
 *
 * It lives on /domains rather than getting a route of its own because domains
 * and providers are one task — "can this deployment send?" — and they already
 * share one attention item and one sending banner. Splitting a setup flow
 * across two screens buys nothing.
 */

import { useState } from "react";

import { api, ApiError } from "../api/client";
import { Icon } from "../components/Sprites";
import { useAsync } from "../hooks";
import { useSession } from "../session";
import type { ProviderConfig, ProviderKind } from "../api/types";

interface CredentialField {
  key: string;
  label: string;
  secret: boolean;
  /** Said under the field: what it is for, when that is not obvious from its name. */
  hint?: string;
}

/** What each adapter needs, taken from internal/providers/factory. */
const CREDENTIAL_FIELDS: Record<ProviderKind, CredentialField[]> = {
  // The configuration set and the topic are required, and the server refuses
  // an SES configuration without them: without the set SES reports nothing,
  // and without the topic every report it sends is refused. The form used to
  // ask for neither, so every SES configuration made here sent mail and never
  // heard what happened to it.
  ses: [
    {
      key: "access_key_id",
      label: "Access key ID",
      secret: false,
      hint: "The IAM user needs ses:SendEmail and ses:SendRawEmail to send, and ses:GetEmailIdentity for the domain check.",
    },
    { key: "secret_access_key", label: "Secret access key", secret: true },
    { key: "region", label: "Region", secret: false },
    {
      key: "configuration_set",
      label: "Configuration set",
      secret: false,
      hint: "The set whose event destination publishes to the SNS topic below.",
    },
    {
      key: "topic_arn",
      label: "SNS topic ARN",
      secret: false,
      hint: "OpenComms accepts events only from this topic.",
    },
    {
      key: "transactional_configuration_set",
      label: "Configuration set for transactional mail (optional)",
      secret: false,
      hint: "A second set with the same topic and no open or click events. Without it, transactional mail is tracked whenever the set above tracks.",
    },
    {
      key: "tenant_name",
      label: "SES tenant (optional)",
      secret: false,
      hint: "Gives this organization its own reputation inside a shared SES account, so SES can pause it without pausing anybody else. Associate the identity and both configuration sets with the tenant in SES first, or every send is refused.",
    },
  ],
  mailgun: [
    { key: "api_key", label: "API key", secret: true },
    { key: "domain", label: "Sending domain", secret: false },
    { key: "webhook_signing_key", label: "Webhook signing key", secret: true },
  ],
  // Postmark signs no webhook, so the user and password in the webhook URL
  // are its whole authentication, and the server refuses every callback
  // without them. The form used to ask for the server token alone.
  postmark: [
    { key: "server_token", label: "Server token", secret: true },
    {
      key: "webhook_user",
      label: "Webhook user",
      secret: false,
      hint: "Put this user and the password below in the webhook URL you give Postmark.",
    },
    { key: "webhook_password", label: "Webhook password", secret: true },
    {
      key: "account_token",
      label: "Account token (optional)",
      secret: true,
      hint: "Postmark shows a domain's DKIM and Return-Path only to the account token. Without it, the Domains screen cannot tell whether they are verified.",
    },
  ],
  // The development catcher takes no credentials — its whole configuration is
  // the SMTP address the server was started with.
  dev: [],
};

const KIND_LABEL: Record<ProviderKind, string> = {
  ses: "Amazon SES",
  mailgun: "Mailgun",
  postmark: "Postmark",
  dev: "Development (Mailpit)",
};

export function Providers() {
  const { orgId, can } = useSession();
  const list = useAsync<ProviderConfig[]>(
    (signal) => api.get<ProviderConfig[]>("/v1/providers", { signal }),
    [orgId],
  );

  const providers = list.data ?? [];
  const manage = can("domains:manage");
  // One rate editor at a time, below the table rather than inside it. The table
  // scrolls sideways under 720px, and an editor inside it was cut off at the
  // panel's edge, its hint readable only by scrolling the table.
  const [editingRate, setEditingRate] = useState<string | null>(null);
  const rateTarget = providers.find((provider) => provider.id === editingRate);

  return (
    <div className="oc-panel">
      <div className="oc-panel__header row row--between">
        <div>
          <div className="oc-headline-md">Sending providers</div>
          <div className="oc-body-sm oc-text-secondary">
            A verified domain says mail from you is legitimate. A provider is what actually
            delivers it — both are needed.
          </div>
          <div className="oc-body-sm oc-text-secondary">
            Campaign opens and clicks are tracked by OpenComms on a domain whose tracking record is
            verified, and by the provider otherwise. Transactional mail is never tracked.
          </div>
        </div>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={list.reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      {list.error && (
        <div className="oc-panel__body">
          <p className="field-error">{list.error.message}</p>
        </div>
      )}

      {providers.length === 0 && !list.loading && (
        <div className="oc-panel__body">
          <div className="oc-empty">
            <div className="oc-empty__title">No provider configured</div>
            <div className="oc-empty__hint">
              Nothing can be delivered until one exists. Add the account you send through — its
              credentials are encrypted before they reach the database.
            </div>
          </div>
        </div>
      )}

      {providers.length > 0 && (
        <div className="oc-table provider-table">
          <div className="oc-table__head" role="row">
            <span>Name</span>
            <span>Provider</span>
            <span className="oc-table__cell--center">Channel</span>
            <span className="oc-table__cell--right">Rate</span>
            <span className="oc-table__cell--right">Default</span>
          </div>
          <div className="oc-table__body">
            {providers.map((provider) => (
              <ProviderRow
                key={provider.id}
                provider={provider}
                canManage={manage}
                onChanged={list.reload}
                editingRate={editingRate === provider.id}
                onEditRate={() =>
                  setEditingRate((current) => (current === provider.id ? null : provider.id))
                }
              />
            ))}
          </div>
        </div>
      )}

      {rateTarget && (
        <RateEditor
          provider={rateTarget}
          onDone={(changed) => {
            setEditingRate(null);
            if (changed) list.reload();
            // Back to the button that opened it. Closing removes the focused
            // element, which leaves a keyboard user at the top of the page.
            const opener = rateButtonId(rateTarget.id);
            requestAnimationFrame(() => document.getElementById(opener)?.focus());
          }}
        />
      )}

      {manage && <AddProvider onAdded={list.reload} />}
    </div>
  );
}

/** Said wherever a rate is chosen: what the number has to match. */
const RATE_HINT =
  "Your provider's maximum send rate. For SES, the rate shown with the account's sending quota. " +
  "Clients sharing one provider account should split its rate between them.";

/** A rate as typed, or null when it is not a whole number of at least one. */
function parseRate(text: string): number | null {
  const value = Number(text);
  return Number.isInteger(value) && value >= 1 ? value : null;
}

function ProviderRow({
  provider,
  canManage,
  onChanged,
  editingRate,
  onEditRate,
}: {
  provider: ProviderConfig;
  canManage: boolean;
  onChanged: () => void;
  /** Whether this row's rate is open in the editor below the table. */
  editingRate: boolean;
  onEditRate: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const promote = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/v1/providers/${provider.id}`, { is_default: true });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not promote this provider.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="oc-table__row" role="row">
      <span className="oc-truncate">{provider.name}</span>
      <span className="oc-text-secondary">{KIND_LABEL[provider.kind] ?? provider.kind}</span>
      <span className="oc-table__cell--center oc-text-secondary">{provider.channel}</span>
      <span className="oc-table__cell--right oc-mono-sm oc-text-secondary">
        {canManage ? (
          <button
            type="button"
            id={rateButtonId(provider.id)}
            className="oc-btn oc-btn--ghost oc-btn--sm oc-mono-sm"
            aria-expanded={editingRate}
            aria-controls={editingRate ? rateEditorId(provider.id) : undefined}
            aria-label={`Send rate ${provider.rate_limit_per_sec} a second. Change it`}
            title="Change the send rate"
            onClick={onEditRate}
          >
            {provider.rate_limit_per_sec}/s
          </button>
        ) : (
          `${provider.rate_limit_per_sec}/s`
        )}
      </span>
      <span className="oc-table__cell--right">
        {provider.is_default ? (
          // The word, not only a colour — the same rule the status badges follow.
          <span className="provider-default">
            <Icon name="check" size={12} />
            Default
          </span>
        ) : canManage ? (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--sm"
            disabled={busy}
            onClick={() => void promote()}
            title={`Send through ${provider.name} from now on`}
          >
            {busy ? "Promoting…" : "Make default"}
          </button>
        ) : (
          <span className="oc-text-muted">—</span>
        )}
        {error && <div className="field-error">{error}</div>}
      </span>
    </div>
  );
}

const rateEditorId = (providerId: string) => `rate-editor-${providerId}`;
const rateButtonId = (providerId: string) => `rate-button-${providerId}`;

/**
 * The rate of one configuration, changed in place: AWS raises an account's rate
 * as it sends well, and matching that must not mean re-entering credentials.
 */
function RateEditor({
  provider,
  onDone,
}: {
  provider: ProviderConfig;
  onDone: (changed: boolean) => void;
}) {
  const [text, setText] = useState(String(provider.rate_limit_per_sec));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rate = parseRate(text);
  const inputId = `rate-${provider.id}`;
  const hintId = `rate-hint-${provider.id}`;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (rate === null) return;
    if (rate === provider.rate_limit_per_sec) {
      onDone(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/v1/providers/${provider.id}`, { rate_limit_per_sec: rate });
      onDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the send rate.");
      setSaving(false);
    }
  };

  return (
    <form
      id={rateEditorId(provider.id)}
      className="oc-panel__body provider-rate-editor"
      onSubmit={(event) => void save(event)}
    >
      <label className="login__field" htmlFor={inputId}>
        <span className="oc-label">Send rate for {provider.name}, a second</span>
        <input
          id={inputId}
          className="oc-input oc-input--sm"
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          value={text}
          aria-invalid={rate === null || Boolean(error)}
          aria-describedby={hintId}
          onChange={(event) => setText(event.target.value)}
          autoFocus
        />
      </label>
      <span id={hintId} className="oc-body-sm oc-text-secondary">
        {RATE_HINT} A send beyond it waits for room; it is never refused.
      </span>
      {error && <p className="field-error">{error}</p>}
      <div className="row" style={{ gap: "var(--oc-space-2)" }}>
        <button type="submit" className="oc-btn oc-btn--sm" disabled={saving || rate === null}>
          {saving ? "Saving…" : "Save rate"}
        </button>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={() => onDone(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddProvider({ onAdded }: { onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<ProviderKind>("ses");
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [isDefault, setIsDefault] = useState(true);
  // 14 is the server's own default, and what a new SES account in production
  // has historically been allowed. Shown rather than left implicit: it decides
  // how fast every campaign through this configuration can go.
  const [rateText, setRateText] = useState("14");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const fields = CREDENTIAL_FIELDS[kind];
  const rate = parseRate(rateText);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const credentials: Record<string, string> = {};
      for (const field of fields) {
        const value = values[field.key]?.trim();
        if (value) credentials[field.key] = value;
      }
      await api.post("/v1/providers", {
        channel: "email",
        kind,
        name: name.trim(),
        is_default: isDefault,
        rate_limit_per_sec: rate,
        credentials,
      });
      setOpen(false);
      setName("");
      setValues({});
      setRateText("14");
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className="oc-panel__body">
        <button type="button" className="oc-btn oc-btn--sm" onClick={() => setOpen(true)}>
          <Icon name="plus" size={12} />
          Add a provider
        </button>
      </div>
    );
  }

  const fieldError = error instanceof ApiError ? error.fields : {};

  return (
    <form className="oc-panel__body stack" onSubmit={(event) => void submit(event)}>
      <div className="row" style={{ gap: "var(--oc-space-3)", alignItems: "flex-end" }}>
        <label className="login__field" style={{ flex: "none", width: 200 }}>
          <span className="oc-label">Provider</span>
          <select
            className="oc-input oc-input--sm"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as ProviderKind);
              setValues({});
            }}
          >
            {(Object.keys(KIND_LABEL) as ProviderKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>

        <label className="login__field grow">
          <span className="oc-label">Name</span>
          <input
            className="oc-input oc-input--sm"
            value={name}
            placeholder="Postmark (production)"
            aria-invalid={Boolean(fieldError["/name"])}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
      </div>

      {fields.length > 0 && (
        <>
          {/* Said before submitting, not after.
              Somebody pasting an SES secret is deciding what to store; learning
              afterwards that the field can never be read back is too late to
              affect where they keep their own copy. */}
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Credentials are encrypted before they reach the database and{" "}
            <strong>can never be read back</strong> — not by this dashboard, not by the API. Keep
            your own copy. Replacing them later means adding a new configuration.
          </p>

          <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
            {fields.map((field) => (
              <label key={field.key} className="login__field">
                <span className="oc-label">{field.label}</span>
                <input
                  className="oc-input oc-input--sm"
                  type={field.secret ? "password" : "text"}
                  autoComplete="off"
                  value={values[field.key] ?? ""}
                  aria-invalid={Boolean(
                    fieldError["/credentials/" + field.key] ?? fieldError["/credentials"],
                  )}
                  onChange={(event) =>
                    setValues((current) => ({ ...current, [field.key]: event.target.value }))
                  }
                />
                {field.hint && <span className="oc-body-sm oc-text-secondary">{field.hint}</span>}
              </label>
            ))}
          </div>
        </>
      )}

      {kind === "dev" && (
        <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
          Delivers to the local mail catcher configured on the server. Useful for development;
          never for real mail.
        </p>
      )}

      <label className="login__field" style={{ maxWidth: 200 }}>
        <span className="oc-label">Send rate, a second</span>
        <input
          className="oc-input oc-input--sm"
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          value={rateText}
          aria-invalid={rate === null || Boolean(fieldError["/rate_limit_per_sec"])}
          aria-describedby="new-provider-rate-hint"
          onChange={(event) => setRateText(event.target.value)}
        />
      </label>
      <span id="new-provider-rate-hint" className="oc-body-sm oc-text-secondary">
        {RATE_HINT} You can change it later.
      </span>

      <label className="row" style={{ gap: "var(--oc-space-2)" }}>
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(event) => setIsDefault(event.target.checked)}
        />
        <span className="oc-body-sm">Send through this provider from now on</span>
      </label>

      {error && <p className="field-error">{error.message}</p>}

      <div className="row" style={{ gap: "var(--oc-space-2)" }}>
        <button
          type="submit"
          className="oc-btn oc-btn--sm"
          disabled={saving || !name.trim() || rate === null}
        >
          {saving ? "Saving…" : "Add provider"}
        </button>
        <button
          type="button"
          className="oc-btn oc-btn--ghost oc-btn--sm"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
